import './Coupon.css'

import moment from 'moment'
import { useEffect, useState } from 'react'
import { Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap'

import { batchUpdateCoupons, createCoupon, deleteCoupon, getAllCoupons, getCouponById, updateCoupon } from '../../../services/coupon'
import { couponTypes } from './consts'
import { CouponSaveForm } from './CouponSaveForm'
import CouponsDataGrid from './CouponsDataGrid'

const initialCoupon = {
  code: '',
  expiredAt: new Date(),
  refUserId: '',
  familyName: '',
  limitedUses: 100,
  limitedUsesForUser: 1,
  timesUsed: 0,
  totalFinalPayments: 0,
  type: couponTypes.PERCENTAGE,
  value: 0,
  usersUsed: [],
  usersAllowed: [],
  minPayment: 0,
  shippingMethod: 'all',
  productCategory: '',
  notes: '',
  packagesUsed: [],
  country: ['all'],
}

export default function Coupon() {
  const [coupons, setCoupons] = useState([])
  const [coupon, setCoupon] = useState(initialCoupon)
  const [showModal, setShowModal] = useState(false)
  const [showBatchDateModal, setShowBatchDateModal] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [batchExpiredAt, setBatchExpiredAt] = useState(new Date())
  const [selectedCoupons, setSelectedCoupons] = useState([])
  const [couponEditHasFamilyName, setCouponEditHasFamilyName] = useState(false)
  const [loading, setLoading] = useState(false)
  const [batchUpdateLoading, setBatchUpdateLoading] = useState(false)
  const [batchUpdateError, setBatchUpdateError] = useState(null)

  useEffect(() => {
    if (window.location.href.includes('office')) {
      document.getElementById('enable-toolbar').style.display = 'none'
    }
    loadCoupons()
  }, [])

  const loadCoupons = async () => {
    setLoading(true)
    try {
      const data = await getAllCoupons()
      //console.log('loadCoupons - data', data)
      setCoupons(data)
    } catch (error) {
      //console.log('loadCoupons - error', error)
      alert(`Failed! \n ${error.response.data?.msg}`)
    } finally {
      setLoading(false)
    }
  }

  const onToggleSave = () => {
    if (showModal) setCoupon(initialCoupon)
    setShowModal(prevState => !prevState)
    setSelectedCoupons([])
  }

  const onToggleBatchDateModal = () => {
    const currentState = showBatchDateModal
    setShowBatchDateModal(!currentState)

    if (!currentState) {
      setShowConfirmModal(false)
      setBatchUpdateError(null)
    }
  }

  const onToggleConfirmModal = () => {
    setShowConfirmModal(prevState => !prevState)
    setBatchUpdateError(null)
  }

  const handleSubmit = async ev => {
    ev.preventDefault()

    //convert from USD to NIS
    // const currencyObj = await getCurrencyObj();
    // const rate = currencyObj.data.response[0].poalimRate

    const input = {
      ...coupon,
      expiredAt: moment(coupon.expiredAt).format(''),
      // minPayment: coupon.minPayment * rate,
    }

    if (!coupon.id) {
      try {
        const res = await createCoupon(input)
        // console.log('handleSubmit - res', res)
        const responseData = res.data.data

        if (responseData.statusCode === 409) {
          alert(`Failed! ${responseData.response}`)
        } else if (responseData.statusCode === 200) {
          alert(`Coupon created successfully`)
        } else {
          alert(`Failed! Unexpected response: ${responseData.response}`)
        }
      } catch (error) {
        console.log('handleSubmit - err', error)
        alert(`Failed! \n ${error.response.data?.msg}`)
      }
    } else {
      try {
        delete coupon.createdAt
        delete coupon.updatedAt
        await updateCoupon(coupon.id, coupon)
        // console.log('handleSubmit - res', res)
        alert('Coupon Package updated successfully')
      } catch (error) {
        console.log('handleSubmit - err', error)
        alert(`Failed! \n ${error.response.data?.msg}`)
      }
    }
    onToggleSave()
    loadCoupons()
    setCoupon(initialCoupon)
  }

  const handleBatchDateNext = ev => {
    ev.preventDefault()

    onToggleConfirmModal()
  }

  const performBatchUpdate = async () => {
    setBatchUpdateLoading(true)
    setBatchUpdateError(null)
    const formattedDate = moment(batchExpiredAt).format('')

    try {
      await batchUpdateCoupons({
        couponIds: coupons.map(coupon => coupon.id),
        updateData: { expiredAt: formattedDate },
      })

      await loadCoupons()

      setShowConfirmModal(false)
      setShowBatchDateModal(false)
    } catch (error) {
      console.error('Error updating coupon dates:', error)
      const errorMessage = error.response?.data?.msg || error.message || 'An unknown error occurred'
      setBatchUpdateError(errorMessage)
    } finally {
      setBatchUpdateLoading(false)
    }
  }

  const onClickEditCouponsDate = async () => {
    onToggleBatchDateModal()
    return
  }

  const onClickEditCoupon = async couponId => {
    try {
      const coupon = await getCouponById(couponId)
      setCoupon({
        ...coupon,
        expiredAt: moment(coupon.expiredAt).toDate(),
      })
      setCouponEditHasFamilyName(!!coupon.familyName)
      onToggleSave()
    } catch (error) {
      console.log('onClickEditCoupon - error', error)
      alert(`Failed! \n ${error.response.data?.msg}`)
    }
  }

  const onClickDeleteCoupon = async couponId => {
    const isConfirm = window.confirm('Are you sure you want to delete?')
    if (!isConfirm) return
    try {
      await deleteCoupon(couponId)
      alert('Coupon deleted successfully')
    } catch (err) {
      console.error(err)
      alert('Failed!')
    } finally {
      loadCoupons()
    }
  }

  const onClickAssignToFamily = async () => {
    const familyName = prompt('Write family name')
    if (!familyName) return

    try {
      // Use batch update for family name assignment
      await batchUpdateCoupons({
        couponIds: selectedCoupons.map(coupon => coupon.id),
        updateData: { familyName },
      })

      alert('Coupons assigned successfully')
      setSelectedCoupons([])
      loadCoupons()
    } catch (error) {
      console.error('Error assigning family name:', error)
      alert(`Failed to assign family name. ${error.response?.data?.msg || error.message}`)
    }
  }

  return (
    <section className='coupons-container'>
      <Modal size='lg' show={showModal} onHide={onToggleSave}>
        <Modal.Header closeButton></Modal.Header>
        <Modal.Body>
          <CouponSaveForm
            coupon={coupon}
            setCoupon={setCoupon}
            handleSubmit={handleSubmit}
            onToggleSave={onToggleSave}
            hasFamilyNameWhenLoaded={couponEditHasFamilyName}
          />
        </Modal.Body>
      </Modal>

      <Modal
        show={showBatchDateModal}
        onHide={!batchUpdateLoading && onToggleBatchDateModal}
        backdrop={batchUpdateLoading ? 'static' : true}
        keyboard={!batchUpdateLoading}
        style={{ direction: 'ltr', textAlign: 'left' }}
      >
        <Modal.Header closeButton={!batchUpdateLoading}>
          <Modal.Title>Update Expiration Date</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleBatchDateNext}>
            <Form.Group className='mb-3'>
              <Form.Label style={{ direction: 'ltr' }}>New Expiration Date for All Coupons ({coupons.length})</Form.Label>
              <Form.Control
                type='date'
                value={moment(batchExpiredAt).format('YYYY-MM-DD')}
                onChange={e => setBatchExpiredAt(new Date(e.target.value))}
                required
                disabled={batchUpdateLoading}
              />
            </Form.Group>
            <div className='d-flex justify-content-start gap-2'>
              {!batchUpdateLoading ? (
                <>
                  <Button variant='primary' type='submit'>
                    Submit
                  </Button>
                  <Button variant='secondary' onClick={onToggleBatchDateModal}>
                    Cancel
                  </Button>
                </>
              ) : (
                <div className='d-flex align-items-center'>
                  <Spinner animation='border' role='status' size='sm' className='me-2' />
                </div>
              )}
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal
        show={showConfirmModal}
        onHide={!batchUpdateLoading && onToggleConfirmModal}
        backdrop={batchUpdateLoading ? 'static' : true}
        keyboard={!batchUpdateLoading}
        style={{ direction: 'ltr', textAlign: 'left' }}
      >
        <Modal.Header closeButton={!batchUpdateLoading}>
          <Modal.Title>Confirm Update</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to update the expiration date for ALL coupons to {moment(batchExpiredAt).format('MMMM D, YYYY')}?</p>
          <p>This will affect {coupons.length} coupons in the system.</p>

          {batchUpdateError && <div className='alert alert-danger mt-3'>Error: {batchUpdateError}</div>}

          <div className='d-flex justify-content-start mt-4 gap-2'>
            {!batchUpdateLoading ? (
              <>
                <Button variant='danger' onClick={performBatchUpdate}>
                  Yes, Update All Coupons
                </Button>
                <Button variant='secondary' onClick={onToggleConfirmModal}>
                  Cancel
                </Button>
              </>
            ) : (
              <div className='d-flex align-items-center'>
                <Spinner animation='border' role='status' size='sm' className='me-2' />
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>

      <h3 className='main-title'>Coupons Management</h3>
      <Row>
        <Col>
          <Button variant='danger' onClick={onToggleSave} disabled={loading}>
            Create New Coupon
          </Button>
        </Col>

        <Col>
          <Button variant='danger' onClick={() => onClickEditCouponsDate()} disabled={loading}>
            Edit Coupons Date
          </Button>
        </Col>
        <Col>
          <Button variant='danger' onClick={onClickAssignToFamily} disabled={!selectedCoupons.length || loading}>
            Assign to family
          </Button>
        </Col>
      </Row>
      <CouponsDataGrid
        loading={loading}
        coupons={coupons}
        onClickEdit={onClickEditCoupon}
        onClickDelete={onClickDeleteCoupon}
        setSelectedCoupons={setSelectedCoupons}
      />
    </section>
  )
}
